import React, { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import Stack from 'src/components/widgets/stack/stack';
import { BasicButton, GhostButton, PrimaryButton } from 'src/components/widgets/buttons';
import { T } from 'src/shared/helpers';
import { AgentAnsweredQuestion, AgentQuestionAnswer, AgentQuestionnaire, AgentQuestionnaireSubmission } from './types';
import { isQuestionAnswered } from './agentInteractionState';

const Card = styled(Stack)`
    flex-shrink: 0;
    min-height: 0;
    max-height: min(480px, 58vh);
    border: 1px solid #dce4e8;
    border-radius: 12px;
    background: white;
    overflow: hidden;
`;
const Body = styled(Stack)`
    min-height: 0;
    padding: 14px;
    overflow-y: auto;
`;
const Heading = styled.div`
    font-size: 13px;
    font-weight: 600;
    color: #263238;
`;
const Hint = styled.span`
    color: #607d8b;
    font-size: 11px;
`;
const Choices = styled.fieldset`
    min-width: 0;
    padding: 0;
    margin: 0;
    border: 0;
    legend {
        padding: 0 0 10px;
        font-size: 13px;
        color: #37474f;
    }
`;
const Choice = styled.label<{ $selected: boolean }>`
    display: flex;
    align-items: flex-start;
    gap: 9px;
    padding: 10px;
    border: 1px solid ${(props) => (props.$selected ? '#0097a7' : '#e4e9ec')};
    border-radius: 8px;
    background: ${(props) => (props.$selected ? '#eef9fa' : 'white')};
    cursor: pointer;
    font-size: 12px;
    input {
        flex-shrink: 0;
        margin: 2px 0 0;
        accent-color: #00899d;
    }
    span {
        min-width: 0;
    }
    strong {
        display: block;
        font-weight: 600;
        color: #263238;
    }
    small {
        display: block;
        color: #607d8b;
        font-size: 11px;
        line-height: 1.5;
    }
`;
const FreeText = styled.textarea`
    box-sizing: border-box;
    width: 100%;
    min-height: 64px;
    padding: 10px;
    border: 1px solid #cfd8dc;
    border-radius: 7px;
    font: inherit;
    font-size: 12px;
    resize: vertical;
    &:focus {
        outline: 2px solid #00899d;
        outline-offset: 1px;
    }
`;
const Footer = styled(Stack)`
    flex-wrap: wrap;
    padding: 10px 14px;
    border-top: 1px solid #e4e9ec;
    align-items: center;
    justify-content: space-between;
`;
const Actions = styled(Stack)`
    flex-wrap: wrap;
    align-items: center;
`;
const ErrorText = styled.span`
    color: #b3261e;
    font-size: 12px;
`;

interface Props {
    conversationId: number;
    questionnaire: AgentQuestionnaire;
    disabled: boolean;
    initialAnswers?: AgentAnsweredQuestion[];
    correctionAfterTurnId?: number;
    onSubmit: (submission: AgentQuestionnaireSubmission) => Promise<boolean | undefined>;
    onCancel?: () => void;
}

export const AgentQuestionWizard: FC<Props> = ({ conversationId, questionnaire, disabled, initialAnswers, correctionAfterTurnId, onSubmit, onCancel }) => {
    const storageKey = `zakeke.agent.questions.${conversationId}.${questionnaire.turnId}.${correctionAfterTurnId ?? 'new'}`;
    const [answers, setAnswers] = useState<AgentQuestionAnswer[]>(() => {
        try {
            const stored = JSON.parse(window.sessionStorage.getItem(storageKey) ?? 'null');
            if (
                Array.isArray(stored) &&
                stored.length === questionnaire.questions.length &&
                stored.every(
                    (a, i) =>
                        a?.questionId === questionnaire.questions[i].id &&
                        Array.isArray(a.optionIds) &&
                        a.optionIds.every((id: unknown) => typeof id === 'string') &&
                        (a.text === null || typeof a.text === 'string') &&
                        typeof a.skipped === 'boolean'
                )
            )
                return stored;
        } catch {
            /* Storage is optional; answering must still work without it. */
        }
        return questionnaire.questions.map((question) => {
            const previous = initialAnswers?.find((a) => a.questionId === question.id);
            return previous
                ? { questionId: previous.questionId, optionIds: previous.optionIds, text: previous.text, skipped: previous.skipped }
                : { questionId: question.id, optionIds: [], text: question.type === 'text' ? '' : null, skipped: false };
        });
    });
    const [index, setIndex] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const question = questionnaire.questions[index];
    const answer = answers[index];
    const locked = disabled || submitting;
    const allAnswered = questionnaire.questions.every((q, i) => isQuestionAnswered(q, answers[i]));
    useEffect(() => {
        try {
            window.sessionStorage.setItem(storageKey, JSON.stringify(answers));
        } catch {
            /* Optional. */
        }
    }, [storageKey, answers]);

    const update = (next: Partial<AgentQuestionAnswer>) => {
        setError(null);
        setAnswers((current) => current.map((a, i) => (i === index ? { ...a, ...next } : a)));
    };
    const submit = async () => {
        if (locked || !allAnswered) return;
        setSubmitting(true);
        setError(null);
        try {
            if (await onSubmit({ turnId: questionnaire.turnId, answers, correctionAfterTurnId })) {
                try {
                    window.sessionStorage.removeItem(storageKey);
                } catch {
                    /* Optional. */
                }
            } else setError(T._('Your answers were not sent. They are still here; please try again.', 'Admin'));
        } catch {
            setError(T._('Your answers were not sent. Please try again.', 'Admin'));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Card $gap='0' aria-label={T._('Clarification questions', 'Admin')}>
            <Body $gap='10px'>
                {(correctionAfterTurnId || question.title.trim() !== question.question.trim()) && (
                    <Heading>{correctionAfterTurnId ? T._('Update your answers', 'Admin') : question.title}</Heading>
                )}
                <Choices disabled={locked}>
                    <legend>{question.question}</legend>
                    <Stack $gap='7px'>
                        {question.options.map((option) => (
                            <Choice key={option.id} $selected={answer.optionIds.includes(option.id)}>
                                <input
                                    type={question.type === 'multiple' ? 'checkbox' : 'radio'}
                                    name={`question-${questionnaire.turnId}-${question.id}`}
                                    checked={answer.optionIds.includes(option.id)}
                                    onChange={() =>
                                        update({
                                            optionIds:
                                                question.type === 'multiple'
                                                    ? answer.optionIds.includes(option.id)
                                                        ? answer.optionIds.filter((id) => id !== option.id)
                                                        : [...answer.optionIds, option.id]
                                                    : [option.id],
                                            text: null,
                                            skipped: false
                                        })
                                    }
                                />
                                <span>
                                    <strong>{option.label}</strong>
                                    {option.description && <small>{option.description}</small>}
                                </span>
                            </Choice>
                        ))}
                        {question.allowFreeText && question.type !== 'text' && (
                            <Choice $selected={answer.text !== null}>
                                <input
                                    type='radio'
                                    name={`question-${questionnaire.turnId}-${question.id}-custom`}
                                    checked={answer.text !== null}
                                    onChange={() => update({ optionIds: [], text: answer.text ?? '', skipped: false })}
                                />
                                <strong>{T._('Write a different answer', 'Admin')}</strong>
                            </Choice>
                        )}
                        {question.allowFreeText && (question.type === 'text' || answer.text !== null) && (
                            <FreeText
                                aria-label={T._('Your answer', 'Admin')}
                                placeholder={T._('Write your answer…', 'Admin')}
                                value={answer.text ?? ''}
                                maxLength={4000}
                                onChange={(event) => update({ optionIds: [], text: event.target.value, skipped: false })}
                            />
                        )}
                    </Stack>
                </Choices>
                {!question.required && (
                    <GhostButton
                        type='button'
                        $size='small'
                        disabled={locked}
                        onClick={() => {
                            update({ optionIds: [], text: null, skipped: true });
                            if (index < questionnaire.questions.length - 1) setIndex(index + 1);
                        }}
                    >
                        {answer.skipped ? T._('Question skipped', 'Admin') : T._('Skip this question', 'Admin')}
                    </GhostButton>
                )}
                {error && <ErrorText role='alert'>{error}</ErrorText>}
            </Body>
            <Footer $horizontal $gap='8px'>
                <Actions $horizontal $gap='8px'>
                    {questionnaire.questions.length > 1 && (
                        <>
                            <BasicButton type='button' $size='small' disabled={locked || index === 0} onClick={() => setIndex(index - 1)}>
                                {T._('Back', 'Admin')}
                            </BasicButton>
                            <Hint>
                                {index + 1} / {questionnaire.questions.length}
                            </Hint>
                        </>
                    )}
                    {onCancel && (
                        <GhostButton type='button' $size='small' disabled={locked} onClick={onCancel}>
                            {T._('Cancel', 'Admin')}
                        </GhostButton>
                    )}
                </Actions>
                {index < questionnaire.questions.length - 1 ? (
                    <PrimaryButton type='button' $size='small' disabled={locked || !isQuestionAnswered(question, answer)} onClick={() => setIndex(index + 1)}>
                        {T._('Next', 'Admin')}
                    </PrimaryButton>
                ) : (
                    <PrimaryButton type='button' $size='small' disabled={locked || !allAnswered} onClick={() => void submit()}>
                        {T._('Send answers', 'Admin')}
                    </PrimaryButton>
                )}
            </Footer>
        </Card>
    );
};
